import React, { useState } from 'react';
import Popup from 'components/Popup';
import ToggleSwitch from 'components/ToggleSwitch';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import {
    ADMINISTRATION_MODES,
    AssignConfig,
    ConfigTest,
    STUDENT_ACCOMMODATION_OPTION,
    TEST_FORMATS,
    TIME_CONSTRAINT,
    configToPayload,
    formatDuration,
    isConfigurable,
    isLegacyShsat,
    isUntimed
} from 'global/testConfig';

import './index.css';

type TestConfigPopupProps = {
    test: ConfigTest;
    initialConfig: AssignConfig;
    // 'assign' creates the assignment on submit; 'update' edits an unstarted one.
    mode: 'assign' | 'update';
    // 'class' adds the "each student's accommodation" timing option.
    audience?: 'student' | 'class';
    // Shown above the buttons, e.g. the re-assign credit charge.
    notice?: string;
    // Receives the request body fields; rejects with the server's error.
    onSubmit: (payload: any) => Promise<unknown>;
    onClose: () => void;
};

function TestConfigPopup({
    test,
    initialConfig,
    mode,
    audience = 'student',
    notice,
    onSubmit,
    onClose
}: TestConfigPopupProps) {
    const [config, setConfig] = useState<AssignConfig>(initialConfig);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const popupRef = useOutsideClickHandler(() => !submitting && onClose());

    const timeOptions = audience === 'class'
        ? [{
            ...STUDENT_ACCOMMODATION_OPTION,
            ...(mode === 'update' ? {
                type: 'Keep current timing',
                description: "Each student keeps the timing they have now"
            } : {})
        }, ...TIME_CONSTRAINT]
        : TIME_CONSTRAINT;

    const update = (changes: Partial<AssignConfig>) => setConfig(current => ({ ...current, ...changes }));

    const toggleSection = (subject: string) => {
        const sections = config.sections.map(section => (
            section.subject === subject ? { ...section, assigned: !section.assigned } : section
        ));
        // At least one section must stay selected.
        if (sections.some(section => section.assigned)) {
            update({ sections });
        }
    };

    const setModuleTwoDifficulty = (subject: string, moduleTwoDifficulty: string) => {
        update({
            sections: config.sections.map(section => (
                section.subject === subject ? { ...section, moduleTwoDifficulty } : section
            ))
        });
    };

    const submit = () => {
        if (submitting) return;
        setSubmitting(true);
        setError('');
        onSubmit(configToPayload(test, config))
            .catch(err => {
                setError(err?.response?.data?.message || 'Something went wrong. Please try again.');
                setSubmitting(false);
            });
    };

    const optionClass = (selected: boolean, disabled = false) => (
        selected
            ? 'testConfig__option--selected'
            : disabled
            ? 'testConfig__option--disabled'
            : 'testConfig__option'
    );

    return (
        <Popup
            title={mode === 'assign' ? `Assign ${test.name}` : `Update ${test.name}`}
            primaryButtonText={submitting ? 'Saving...' : mode === 'assign' ? 'Assign' : 'Update'}
            primaryButtonOnClick={submit}
            primaryButtonDisabled={submitting}
            secondaryButtonText='Cancel'
            secondaryButtonOnClick={onClose}
            innerRef={popupRef}
        >
            {isConfigurable(test) && <>
                <h4 className='testConfig__header'>Test sections</h4>
                <div className='testConfig__options'>
                    {config.sections.map(section => (
                        <button
                            key={section.subject}
                            className={optionClass(section.assigned, section.score !== null)}
                            onClick={() => section.score === null && toggleSection(section.subject)}
                        >
                            <p className='testConfig__optionText'>{section.subject.replace('ACT ', '')}</p>
                            <p className='testConfig__optionDescription'>
                                {['Digital SAT', 'Digital PSAT'].includes(test.type)
                                    ? `All modules from ${section.subject}`
                                    : `${section.questionCount} questions, ${formatDuration(section.testTime || 0)}`
                                }
                            </p>
                        </button>
                    ))}
                </div>
            </>}
            {config.adaptive !== null && <>
                <h4 className='testConfig__header'>Test type</h4>
                <div className='testConfig__options'>
                    {TEST_FORMATS.map(format => (
                        <button
                            key={format.type}
                            className={optionClass(config.adaptive === (format.type === 'Adaptive'))}
                            onClick={() => update({ adaptive: format.type === 'Adaptive' })}
                        >
                            <p className='testConfig__optionText'>{format.type}</p>
                            <p className='testConfig__optionDescription'>{format.description}</p>
                        </button>
                    ))}
                </div>
                {config.adaptive === false && config.sections.filter(section => section.assigned).map(section => (
                    <React.Fragment key={section.subject}>
                        <h4 className='testConfig__header'>Module 2 difficulty : {section.subject}</h4>
                        <div className='testConfig__options'>
                            <button
                                className={optionClass(section.moduleTwoDifficulty === 'Easy')}
                                onClick={() => setModuleTwoDifficulty(section.subject, 'Easy')}
                            >
                                <p className='testConfig__optionText'>Easy</p>
                                <p className='testConfig__optionDescription'>Suggested for score range 200-600</p>
                            </button>
                            <button
                                className={optionClass(section.moduleTwoDifficulty === 'Hard')}
                                onClick={() => setModuleTwoDifficulty(section.subject, 'Hard')}
                            >
                                <p className='testConfig__optionText'>Hard</p>
                                <p className='testConfig__optionDescription'>Suggested for score range 400-700</p>
                            </button>
                        </div>
                    </React.Fragment>
                ))}
            </>}
            {/* TSIA2 has no clock, so there is no timing to choose; say so
                rather than offer options the player would ignore. */}
            {isUntimed(test)
                ? <p className='testConfig__optionDescription'>
                    This test is untimed, as the real {test.type} is. Every section is assigned;
                    the stages a student sees are decided by their own score on the first stage.
                </p>
                : <>
                    <h4 className='testConfig__header'>Choose the type of time constraint for this assignment:</h4>
                    <div className={timeOptions.length > 4 ? 'testConfig__options--five' : 'testConfig__options--four'}>
                        {timeOptions.map(option => (
                            <button
                                key={option.type}
                                className={optionClass(option.value === config.timeConstraint)}
                                onClick={() => update({ timeConstraint: option.value })}
                            >
                                <p className='testConfig__optionText'>{option.type}</p>
                                <p className='testConfig__optionDescription'>{option.description}</p>
                            </button>
                        ))}
                    </div>
                </>
            }
            {/* SHSAT Adaptive only: Adaptive = engine alternates Medium/Hard
                tiers per slot from prior answers; Hard / Medium only = every
                slot served as that variant. */}
            {config.administrationMode !== null && <>
                <h4 className='testConfig__header'>Administration mode</h4>
                <div className='testConfig__options--three'>
                    {ADMINISTRATION_MODES.map(adminMode => (
                        <button
                            key={adminMode.value}
                            className={optionClass(config.administrationMode === adminMode.value)}
                            onClick={() => update({ administrationMode: adminMode.value })}
                        >
                            <p className='testConfig__optionText'>{adminMode.type}</p>
                            <p className='testConfig__optionDescription'>{adminMode.description}</p>
                        </button>
                    ))}
                </div>
            </>}
            {test.ttsEligible && <>
                <h4 className='testConfig__header'>Accommodations:</h4>
                <ToggleSwitch
                    label={audience === 'class'
                        ? 'Allow Read Aloud assistance (applies to all students in class)'
                        : 'Allow Read Aloud assistance'
                    }
                    toggle={config.ttsEnabled}
                    onClick={() => update({ ttsEnabled: !config.ttsEnabled })}
                />
            </>}
            {/* Legacy SHSAT only: locked by default; turning this on hands the
                student the unlocked version (free back-and-forth). */}
            {isLegacyShsat(test) && <>
                <h4 className='testConfig__header'>Navigation:</h4>
                <ToggleSwitch
                    label='Allow free navigation between questions (turn off the lock)'
                    toggle={config.allowFreeNavigation}
                    onClick={() => update({ allowFreeNavigation: !config.allowFreeNavigation })}
                />
            </>}
            {notice && <p className='testConfig__notice'>{notice}</p>}
            {error && <p className='testConfig__error'>{error}</p>}
        </Popup>
    );
}

export default TestConfigPopup;
